import { c as _c } from "react/compiler-runtime";
import { TableHead as KuramaTableHead } from '@precis-digital/kurama/Table';
import { styled } from '@precis-digital/kurama/utils';
import type { ColumnFiltersState, ColumnPinningState, ColumnSizingInfoState, RowSelectionState, SortingState } from '@tanstack/react-table';
import type { Dispatch, ReactElement, RefObject } from 'react';
import type { ChartCardStateAction, ElementsDescriptionsMap } from '../../../internalTypes';
import type { ChartCardTFunction, ChartDataRow } from '../../../types';
import { Z_INDEX } from '../constants';
import TableHeadRow from './TableHeadRow';
import type { ColorScaleColumnMap, ColumnSetDisplayColorScaleFn, ColumnSetSortFn } from '../types';
import type { ChartCardTable } from '../tanstack';
import { buildKey } from '../utils';
interface TableHeadProps<DataKey extends string, DataRow extends ChartDataRow<DataKey>> {
  elementsDescriptionsMap: ElementsDescriptionsMap<DataKey>;
  rowSelection: RowSelectionState;
  sorting: SortingState;
  selectedColumnsIds: string[];
  columnPinning: ColumnPinningState;
  columnFilters: ColumnFiltersState;
  isResizingColumn: ColumnSizingInfoState['isResizingColumn'];
  resizerCursorUrl: string | null;
  table: ChartCardTable<DataKey, DataRow>;
  setSort: ColumnSetSortFn;
  colorScaleColumnMap: ColorScaleColumnMap;
  setDisplayColorScale: ColumnSetDisplayColorScaleFn;
  useColumnsDecorations: boolean;
  allowColumnFiltering: boolean;
  allowDateFiltering: boolean;
  onOpenColumnFilter?: (columnId: string) => void;
  allowColumnResizing: boolean;
  allowColumnAutoResizing: boolean;
  allowColumnPinning: boolean;
  allowColumnReordering: boolean;
  dispatch: Dispatch<ChartCardStateAction<DataKey, DataRow>>;
  t: ChartCardTFunction;
  headerRootRef?: RefObject<HTMLTableSectionElement | null>;
  /** When true, header ignores pointer interaction without changing visuals. */
  isFetchLoading?: boolean;
}
const TableHead = t0 => {
  const $ = _c(49);
  const {
    elementsDescriptionsMap,
    table: t1,
    rowSelection,
    sorting,
    setSort,
    selectedColumnsIds,
    columnPinning,
    columnFilters,
    isResizingColumn,
    resizerCursorUrl,
    colorScaleColumnMap,
    setDisplayColorScale,
    useColumnsDecorations,
    allowColumnFiltering,
    allowDateFiltering,
    onOpenColumnFilter,
    allowColumnResizing,
    allowColumnAutoResizing,
    allowColumnPinning,
    allowColumnReordering,
    dispatch,
    t,
    headerRootRef,
    isFetchLoading
  } = t0;
  const {
    getHeaderGroups
  } = t1;
  const t2 = isFetchLoading === true;
  let t3;
  if ($[0] !== allowColumnAutoResizing || $[1] !== allowColumnFiltering || $[2] !== allowColumnPinning || $[3] !== allowColumnReordering || $[4] !== allowColumnResizing || $[5] !== allowDateFiltering || $[6] !== colorScaleColumnMap || $[7] !== columnFilters || $[8] !== columnPinning || $[9] !== dispatch || $[10] !== elementsDescriptionsMap || $[11] !== getHeaderGroups || $[12] !== isResizingColumn || $[13] !== onOpenColumnFilter || $[14] !== resizerCursorUrl || $[15] !== rowSelection || $[16] !== selectedColumnsIds || $[17] !== setDisplayColorScale || $[18] !== setSort || $[19] !== sorting || $[20] !== t || $[21] !== useColumnsDecorations) {
    let t4;
    if ($[23] !== allowColumnAutoResizing || $[24] !== allowColumnFiltering || $[25] !== allowColumnPinning || $[26] !== allowColumnReordering || $[27] !== allowColumnResizing || $[28] !== allowDateFiltering || $[29] !== colorScaleColumnMap || $[30] !== columnFilters || $[31] !== columnPinning || $[32] !== dispatch || $[33] !== elementsDescriptionsMap || $[34] !== isResizingColumn || $[35] !== onOpenColumnFilter || $[36] !== resizerCursorUrl || $[37] !== rowSelection || $[38] !== selectedColumnsIds || $[39] !== setDisplayColorScale || $[40] !== setSort || $[41] !== sorting || $[42] !== t || $[43] !== useColumnsDecorations) {
      t4 = headerGroup => <TableHeadRow key={buildKey(headerGroup.id, selectedColumnsIds, columnPinning)} headerGroup={headerGroup} elementsDescriptionsMap={elementsDescriptionsMap} rowSelection={rowSelection} sorting={sorting} setSort={setSort} columnPinning={columnPinning} columnFilters={columnFilters} isResizingColumn={isResizingColumn} resizerCursorUrl={resizerCursorUrl} colorScaleColumnMap={colorScaleColumnMap} setDisplayColorScale={setDisplayColorScale} useColumnsDecorations={useColumnsDecorations} allowColumnFiltering={allowColumnFiltering} allowDateFiltering={allowDateFiltering} onOpenColumnFilter={onOpenColumnFilter} allowColumnResizing={allowColumnResizing} allowColumnAutoResizing={allowColumnAutoResizing} allowColumnPinning={allowColumnPinning} allowColumnReordering={allowColumnReordering} dispatch={dispatch} t={t} />;
      $[23] = allowColumnAutoResizing;
      $[24] = allowColumnFiltering;
      $[25] = allowColumnPinning;
      $[26] = allowColumnReordering;
      $[27] = allowColumnResizing;
      $[28] = allowDateFiltering;
      $[29] = colorScaleColumnMap;
      $[30] = columnFilters;
      $[31] = columnPinning;
      $[32] = dispatch;
      $[33] = elementsDescriptionsMap;
      $[34] = isResizingColumn;
      $[35] = onOpenColumnFilter;
      $[36] = resizerCursorUrl;
      $[37] = rowSelection;
      $[38] = selectedColumnsIds;
      $[39] = setDisplayColorScale;
      $[40] = setSort;
      $[41] = sorting;
      $[42] = t;
      $[43] = useColumnsDecorations;
      $[44] = t4;
    } else {
      t4 = $[44];
    }
    t3 = getHeaderGroups().map(t4);
    $[0] = allowColumnAutoResizing;
    $[1] = allowColumnFiltering;
    $[2] = allowColumnPinning;
    $[3] = allowColumnReordering;
    $[4] = allowColumnResizing;
    $[5] = allowDateFiltering;
    $[6] = colorScaleColumnMap;
    $[7] = columnFilters;
    $[8] = columnPinning;
    $[9] = dispatch;
    $[10] = elementsDescriptionsMap;
    $[11] = getHeaderGroups;
    $[12] = isResizingColumn;
    $[13] = onOpenColumnFilter;
    $[14] = resizerCursorUrl;
    $[15] = rowSelection;
    $[16] = selectedColumnsIds;
    $[17] = setDisplayColorScale;
    $[18] = setSort;
    $[19] = sorting;
    $[20] = t;
    $[21] = useColumnsDecorations;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[45] !== headerRootRef || $[46] !== t2 || $[47] !== t3) {
    t4 = <StyledTableHead ref={headerRootRef} isInteractionDisabled={t2}>{t3}</StyledTableHead>;
    $[45] = headerRootRef;
    $[46] = t2;
    $[47] = t3;
    $[48] = t4;
  } else {
    t4 = $[48];
  }
  return t4;
};
const StyledTableHead = styled(KuramaTableHead, {
  shouldForwardProp: prop => prop !== 'isInteractionDisabled'
})<{
  isInteractionDisabled?: boolean;
}>(({
  isInteractionDisabled
}) => ({
  display: 'grid',
  order: 1,
  position: 'sticky',
  top: 0,
  zIndex: Z_INDEX.head,
  ...(isInteractionDisabled === true && {
    pointerEvents: 'none'
  })
}));
export default TableHead;